import React, {
    useState,
    useEffect,
    useRef,
    ReactElement,
} from 'react';
import RawSlider from 'rc-slider';
import 'rc-slider/assets/index.css';

import Label from '@/components/forms/label';
import Description from '@/components/forms/description';

import modalStyles from './ModalStyles.module.css';

type SliderProps = {
    readonly defaultValue: number,
    readonly description?: string,
    readonly disabled?: boolean,
    readonly error?: boolean,
    readonly errorText?: string,
    readonly label: string | ReactElement,
    readonly labelClass?: string,
    readonly name: string,
    readonly options: Array<string>,
    readonly externalStyles: string,
    readonly showSliderConfirm?: boolean,
    readonly onChange?: (value1: any, value2: number, value3: boolean) => void,
};

function Slider({
    defaultValue,
    description = '',
    disabled = false,
    error = false,
    errorText = '',
    label,
    labelClass = '',
    name,
    options,
    externalStyles,
    showSliderConfirm = false,
    onChange = () => { },
}: SliderProps) {
    const [maxValue, setMaxValue] = useState(null);
    const [marks, setMarks] = useState(null);
    const sliderRef = useRef(null);

    const finalClass = `${modalStyles.slider} ${disabled && modalStyles.disabled} ${error && modalStyles.error} ${externalStyles}`;

    useEffect(() => {
        let max = 0;
        const newMarks = {};
        const markClassName = `text-bold text-base ${modalStyles.mark} ${error && modalStyles.error}`;
        options.forEach((opt, idx) => {
            const color = 'text-cleanSlate';
            const finalMarkClassName = `break-none ${markClassName} ${color}`;
            newMarks[idx] = {
                label: (<span className={finalMarkClassName}>{opt}</span>),
            };
            max = idx;
        });

        if (max > 0) {
            setMaxValue(max);
            setMarks(newMarks);
        }
    }, [options, error]);

    const onSliderChange = () => (value: number) => {
        onChange(null, value, true);
    };

    return (
        <div>
            <Label
                className={labelClass}
                htmlFor={name}
                label={label}
                onClick={() => {
                    sliderRef?.current?.focus();
                }}
            />
            <div>
                {(maxValue !== null)
                    && (
                        <div className={modalStyles.container}>
                            <RawSlider
                                ref={sliderRef}
                                className={finalClass}
                                railStyle={{
                                    backgroundColor: 'var(--color-lightGray)',
                                }}
                                dotStyle={{
                                    display: 'none',
                                }}
                                handleStyle={{
                                    backgroundColor: 'white',
                                    borderColor: 'var(--color-lightGray)',
                                    boxShadow: '0px 2px 4px -2px black !important',
                                    opacity: 1,
                                }}
                                trackStyle={{
                                    backgroundColor: `${showSliderConfirm ? 'var(--color-green)' : 'var(--color-gray)'}`,
                                    height: '16px',
                                    top: '0px',
                                }}
                                defaultValue={defaultValue}
                                min={0}
                                step={1}
                                max={maxValue}
                                disabled={disabled}
                                marks={marks}
                                onChange={onSliderChange()}
                            />
                        </div>
                    )}
                <br className={modalStyles.spacer} />
                <Description description={description} />
                {error && errorText.length > 0 && (
                    <div className={modalStyles.errorText}>
                        {errorText}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Slider;
